import { parse } from "papaparse";
import { downloadCsvFile } from "../../../utils/csvDownload";
import { createEmptyRow, isImageHttpUrl, isRowEmpty } from "./produceRows";

const normalizeHeader = ({ value }) =>
  String(value || "")
    .trim()
    .toLowerCase();

const stripDiacritics = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const toSafeFileName = ({ name }) => {
  const safe = stripDiacritics(name || "plantilla")
    .trim()
    .replace(/[^\w.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return safe || "plantilla";
};

const buildHeaderMap = ({ columns, headers = [] }) => {
  const map = {};
  columns.forEach((column) => {
    const match = headers.find((header) => {
      const normalized = normalizeHeader({ value: header });
      return (
        normalized === normalizeHeader({ value: column.key }) ||
        normalized === normalizeHeader({ value: column.label })
      );
    });
    if (match) map[column.key] = match;
  });
  return map;
};

const mapCsvValue = ({ value, type }) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  if (type === "image" && !isImageHttpUrl({ value: text })) return "";
  return text;
};

const mapCsvRow = ({ raw = {}, columns, headerMap }) => {
  const row = createEmptyRow({ columns });
  columns.forEach((column) => {
    const header = headerMap[column.key];
    row[column.key] = mapCsvValue({
      value: header ? raw[header] : "",
      type: column.type,
    });
  });
  return row;
};

export const parseProduceCsvFile = ({ file, columns }) =>
  new Promise((resolve, reject) => {
    parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      complete: (results) => {
        const headerMap = buildHeaderMap({
          columns,
          headers: results.meta?.fields || [],
        });
        const rows = (results.data || [])
          .map((raw) => mapCsvRow({ raw, columns, headerMap }))
          .filter((row) => !isRowEmpty({ row, columns }));
        resolve(rows);
      },
      error: (error) => reject(error),
    });
  });

export const getProduceColumnExampleText = ({ column, exampleText } = {}) => {
  if (column?.type === "image") return "";
  const key = String(column?.key || "campo");
  const origin = String(column?.originExample || "").trim();
  if (origin) return origin;
  const label = String(column?.label || key).replace(/_/g, " ");
  return exampleText ? exampleText({ label }) : `Example ${label}`;
};

export const createExampleRow = ({ columns = [], exampleText } = {}) => {
  const row = {};
  columns.forEach((column) => {
    const key = String(column.key || "campo");
    if (column.type === "image") {
      const origin = String(column.originExample || "").trim();
      row[column.key] = isImageHttpUrl({ value: origin })
        ? origin
        : `https://example.com/${key}.jpg`;
      return;
    }
    row[column.key] = getProduceColumnExampleText({ column, exampleText });
  });
  return row;
};

export const downloadProduceFormatCsv = ({
  columns,
  templateName,
  fileName,
  exampleText,
}) =>
  downloadCsvFile({
    fields: columns.map((column) => column.key),
    data: [createExampleRow({ columns, exampleText })],
    fileName,
    fallbackName: `${toSafeFileName({
      name: templateName,
    })}-example-variables.csv`,
  });

export const downloadProduceRowsCsv = ({
  columns,
  rows = [],
  templateName,
  fileName,
}) => {
  const fields = columns.map((column) => column.key);
  return downloadCsvFile({
    fields,
    data: rows.map((row) => fields.map((key) => String(row?.[key] ?? ""))),
    fileName,
    fallbackName: `${toSafeFileName({ name: templateName })}-variables.csv`,
  });
};
